import React, { useMemo, useState } from 'react';
import { Users, Plus, Trash2, QrCode, Smartphone, RefreshCw, Clock, CheckCircle, XCircle, Crown } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { useSelectedUnit, useToast, useDeleteConfirmation } from '@/app/providers';
import { Button } from '@/core/ui/components/Button';
import type { User, HouseholdMember } from '@/shared/types';
import { FamilyMemberModal } from './FamilyMemberModal';
import { MemberQRModal } from './MemberQRModal';

interface FamilySectionProps {
  user: User;
  onAddMember: (member: Omit<HouseholdMember, 'id' | 'accountStatus'>) => Promise<void>;
  onDeleteMember: (memberId: string) => Promise<void>;
  onResendInvitation?: (memberId: string) => Promise<void>;
}

export const FamilySection: React.FC<FamilySectionProps> = ({ user, onAddMember, onDeleteMember, onResendInvitation }) => {
  const { t } = useI18n();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { selectedUnitId, hasMultipleUnits } = useSelectedUnit();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [memberQrData, setMemberQrData] = useState<HouseholdMember | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Con más de una unidad, la lista y el permiso de agregar dependen de la
  // casa activa en el sidebar, no de "es principal de alguna unidad" en general.
  const visibleMembers = useMemo(() => {
    if (!hasMultipleUnits) return user.householdMembers ?? [];
    return (user.householdMembers ?? []).filter((member) => member.unitId === selectedUnitId);
  }, [user.householdMembers, hasMultipleUnits, selectedUnitId]);

  const isPrincipalOfActiveUnit = hasMultipleUnits
    ? (user.units?.find((assignment) => assignment.unitId === selectedUnitId)?.isPrincipal ?? false)
    : (user.isPrincipal ?? false);

  const handleResendInvitation = async (member: HouseholdMember) => {
    if (!onResendInvitation) return;
    setResendingId(member.id);
    try {
      await onResendInvitation(member.id);
      toast.success(`Se ha reenviado la invitación a ${member.email}`);
    } catch {
      toast.error('Error al reenviar la invitación');
    } finally {
      setResendingId(null);
    }
  };

  const getStatusBadge = (status: HouseholdMember['accountStatus']) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
            <CheckCircle size={12} /> Activo
          </span>
        );
      case 'PENDING_VERIFICATION':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
            <Clock size={12} /> Pendiente
          </span>
        );
      case 'INACTIVE':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
            <XCircle size={12} /> Inactivo
          </span>
        );
      default:
        return null;
    }
  };

  const handleDelete = async (memberId: string, memberName: string) => {
    await confirmDelete({
      itemName: memberName,
      itemType: 'Miembro del hogar',
      onConfirm: async () => {
        await onDeleteMember(memberId);
      }
    });
  };

  return (
    <div className="space-y-6 animate-in fade-in">
      <div className="flex justify-between items-center">
        <p className="text-slate-500 text-sm">
          {isPrincipalOfActiveUnit
            ? 'Miembros registrados en esta unidad.'
            : 'Miembros registrados en esta unidad. Solo el residente principal puede agregar nuevos miembros.'}
        </p>
        {isPrincipalOfActiveUnit && (
          <Button size="sm" icon={<Plus size={16} />} onClick={() => setIsModalOpen(true)}>Agregar Miembro</Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visibleMembers.map(member => {
          const isActive = member.accountStatus === 'ACTIVE';
          // Co-residentes surfaced desde tenant_members (editable === false) no tienen
          // un id resoluble por los endpoints de household-members: no se pueden borrar
          // ni reenviarles invitación.
          const isEditable = member.editable !== false;
          const canResend = isEditable && !isActive && Boolean(member.email) && Boolean(onResendInvitation);
          return (
            <div key={member.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-lg">
                    {member.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-slate-800">{member.name}</h4>
                      {member.isPrincipal && (
                        <span title="Residente principal de esta unidad">
                          <Crown size={13} className="fill-primary-400 text-primary-500 shrink-0" />
                        </span>
                      )}
                      {getStatusBadge(member.accountStatus)}
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                      <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-600 font-medium">{t(`profile.relation.${member.relation.toLowerCase()}` as any)}</span>
                      {member.age && <span>{member.age} años</span>}
                    </p>
                    {member.email && (
                      <p className="text-xs text-slate-400 mt-1">{member.email}</p>
                    )}
                  </div>
                </div>
                {isEditable && isPrincipalOfActiveUnit && (
                  <button onClick={() => handleDelete(member.id, member.name)} className="text-slate-300 hover:text-red-500 transition-colors p-2">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {/* Reenviar invitación y ver QR son acciones de gestión — solo
                  el principal de la unidad puede verlas, igual que agregar/borrar. */}
              {isPrincipalOfActiveUnit && (
                <div className="flex gap-2 border-t border-slate-100 pt-3">
                  {canResend ? (
                    <button
                      onClick={() => handleResendInvitation(member)}
                      disabled={resendingId === member.id}
                      className="flex-1 flex items-center justify-center gap-2 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <RefreshCw size={14} className={resendingId === member.id ? 'animate-spin' : ''} />
                      {resendingId === member.id ? 'Enviando...' : 'Reenviar Invitación'}
                    </button>
                  ) : isActive ? (
                    <button
                      onClick={() => setMemberQrData(member)}
                      className="flex-1 flex items-center justify-center gap-2 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 py-2 rounded-lg transition-colors"
                    >
                      <Smartphone size={14} /> Cuenta Activa
                    </button>
                  ) : (
                    <button
                      onClick={() => setMemberQrData(member)}
                      className="flex-1 flex items-center justify-center gap-2 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 py-2 rounded-lg transition-colors"
                    >
                      <QrCode size={14} /> Ver QR Acceso
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {visibleMembers.length === 0 && (
          <div className="col-span-2 text-center p-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <Users size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-400 text-sm italic">No hay familiares registrados.</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <FamilyMemberModal
          onClose={() => setIsModalOpen(false)}
          onSave={(member) => onAddMember(hasMultipleUnits && selectedUnitId ? { ...member, unitId: selectedUnitId } : member)}
        />
      )}

      {memberQrData && (
        <MemberQRModal
          member={memberQrData}
          residenceInfo={user.residenceInfo}
          onClose={() => setMemberQrData(null)}
        />
      )}
    </div>
  );
};
